import React, { useCallback, useEffect, useState } from 'react';
import { dispatchGlobalError } from '../../context/ApiErrorContext';
import PartidaCardEditor from './PartidaCardEditor';
import {
  FaseCategoriaTorneio,
} from '../../services/categoriaTorneioService';
import {
  PartidaInput,
  atualizarPartida,
  deletarPartida,
} from '../../services/partidaService';
import { PartidaLite } from '../../services/faseGrupoPartidasService';
import { buscarEquipesPorCategoriaTorneio } from '../../services/equipeService';
import { Local } from '../../types/local';
import { Equipe } from '../../types/equipe';

export type FaseChaveColumnProps = {
  fase: FaseCategoriaTorneio;
  categoriaTorneioId: string;
  localTorneio: Local;
  faseId: string;
  categoriaTorneio?: {
    gamesPorSets?: number | null;
    tiebreaks?: number | null;
    numeroSets?: number | null;
  } | null;
  onNovaPartida?: (faseId: string) => void;
  onSaved?: () => void;
};

const FaseChaveColumn: React.FC<FaseChaveColumnProps> = ({
  fase,
  categoriaTorneioId,
  localTorneio,
  faseId,
  categoriaTorneio,
  onNovaPartida,
  onSaved,
}) => {
  const partidas = [...(fase.partidas ?? [])].sort((a, b) => {
    if (a.numeroPartida != null && b.numeroPartida != null) {
      return a.numeroPartida - b.numeroPartida;
    }
    if (a.numeroPartida != null) return -1;
    if (b.numeroPartida != null) return 1;
    return new Date(a.data).getTime() - new Date(b.data).getTime();
  });

  const [equipes, setEquipes] = useState<Equipe[]>([]);
  const [loadingEquipes, setLoadingEquipes] = useState<boolean>(false);

  const carregarEquipes = useCallback(async () => {
    if (!categoriaTorneioId) return;
    setLoadingEquipes(true);
    try {
      const lista = await buscarEquipesPorCategoriaTorneio(categoriaTorneioId);
      setEquipes(lista ?? []);
    } catch (e) {
      dispatchGlobalError('Não foi possível carregar as equipes desta categoria.');
    } finally {
      setLoadingEquipes(false);
    }
  }, [categoriaTorneioId]);

  useEffect(() => {
    carregarEquipes();
  }, [carregarEquipes]);

  const handleSavePartida = async (p: Partial<PartidaInput> & { id: string }) => {
    const input = {
      data: p.data || null,
      localId: p.localId || null,
      categoriaTorneioId: categoriaTorneioId || null,
      faseCategoriaTorneioId: fase.id || null,
      equipeAId: p.equipeAId || null,
      equipeBId: p.equipeBId || null,
      status: p.status || null,
      placar: p.placar || null,
      equipeAWO: p.equipeAWO || null,
      equipeBWO: p.equipeBWO || null,
      equipeADesistencia: p.equipeADesistencia || null,
      equipeBDesistencia: p.equipeBDesistencia || null,
    };
    p.categoriaTorneioId = categoriaTorneioId;
    p.faseCategoriaTorneioId = fase.id;
    await atualizarPartida(p.id, input);
    onSaved?.();
  };

  const handleDeletePartida = async (id: string) => {
    if (!window.confirm('Deseja excluir a partida?')) return;
    await deletarPartida(id);
    onSaved?.();
  };

  return (
    <div className="d-flex flex-column gap-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="mb-0">{fase.nome}</h5>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={() => onNovaPartida?.(faseId)}
        >
          Nova partida +
        </button>
      </div>
      {partidas.length === 0 ? (
        <div> Nenhuma partida nesta fase. </div>
      ) : (
        partidas.map((partida, index) => (
          <div key={partida.id} data-testid={`partida-fase-chave-${partida.id}`}>
            <PartidaCardEditor
              partida={partida as PartidaLite}
              index={index}
              equipes={equipes}
              loadingEquipes={loadingEquipes}
              onCarregarEquipes={carregarEquipes}
              onSave={handleSavePartida}
              onDelete={handleDeletePartida}
              localTorneio={localTorneio}
              categoriaTorneio={categoriaTorneio}
            />
          </div>
        ))
      )}
    </div>
  );
};

export default FaseChaveColumn;

